import React, { useEffect, useRef } from 'react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  TableOptions,
  usePagination,
  useRowSelect,
  useTable,
} from 'react-table';
import Loader from '../loader';
import NoDataShown from '../noResult';

interface IProps {
  loading?: boolean;
  tableData: any[];
  tableColumn: any;
  text?: string;
  pagination?: boolean;
  onSelectRow?: (row: any) => void;
  selectedRows?: Set<number>;
  showCheckbox?: boolean;
  className?: string;
  serverPagination?: {
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    setPageIndex: (val: number) => void;
    setPageSize: (val: number) => void;
  };
}

interface MyTableOptions<D extends object> extends TableOptions<D> {
  autoResetPage?: boolean;
}

const Table: React.FC<IProps> = ({
  loading,
  tableData,
  tableColumn,
  pagination,
  onSelectRow,
  showCheckbox,
  serverPagination,
  className
}) => {
  const isServerPagination = !!serverPagination;
  const data = React.useMemo(() => (tableData ? tableData : []), [tableData]);
  const columns = React.useMemo(() => tableColumn, []);
  const navigate = useNavigate();
  const tableProps = isServerPagination ? {
    data,
    columns,
    autoResetPage: false,
    manualPagination: !!serverPagination,
    pageCount: !!serverPagination ? serverPagination.totalPages : undefined,
    initialState: {
      pageIndex: !!serverPagination ? serverPagination.pageIndex : 0,
      pageSize: !!serverPagination ? serverPagination.pageSize : 10,
    },
  }
  : 
  {
    data,
    columns,
    autoResetPage: false,
  } 
  
  const {
    headerGroups,
    getTableBodyProps,
    page,
    prepareRow,
    getTableProps,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize, selectedRowIds },
  }: any = useTable(
    tableProps as TableOptions<any>,
    usePagination,
    useRowSelect,
    ...(showCheckbox
      ? [
          (hooks: any) => {
            hooks.visibleColumns.push((columns: any) => [
              {
                id: 'selection',
                Header: ({ getToggleAllRowsSelectedProps }: any) => (
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600"
                    {...getToggleAllRowsSelectedProps()}
                  />
                ),
                Cell: ({ row }: any) => (
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600"
                    {...row.getToggleRowSelectedProps()}
                  />
                ),
              },
              ...columns,
            ]);
          },
        ]
      : [])
  );

  useEffect(() => {
    if (!onSelectRow) return;
    const selectedData = selectedFlatRows.map((row: any) => row.original);
    const prevSelected = JSON.stringify(prevSelectedRowsRef.current);
    const currentSelected = JSON.stringify(selectedData);
    if (prevSelected !== currentSelected) {
      prevSelectedRowsRef.current = selectedData;
      onSelectRow(selectedData);
    }
  }, [selectedFlatRows, onSelectRow]);

  const prevSelectedRowsRef = useRef<any[]>([]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="w-full">
            <div className={`mt-5 rounded-xl border border-border-color-dark overflow-x-hidden relative  w-full  ${className}`}>
              <table
                {...getTableProps()}
                className="w-full text-left text-text-black table-fixed"
              >
                <thead className="font-semibold text-black border-b border-border-color-dark block w-full">
                  {headerGroups.map((headerGroup: any, i: number) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      key={i}
                      className="flex w-full"
                    >
                      {headerGroup.headers.map((column: any, idx: number) => (
                        <th
                          {...column.getHeaderProps()}
                          key={idx}
                          scope="col"
                          className="py-3 px-2 xl:px-4 text-sm whitespace-nowrap flex-1"
                        >
                          <div className="flex items-center">
                            <span className="text-xs xl:text-base">
                              {column.render('Header')}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody
                  {...getTableBodyProps()}
                  className="block w-full max-h-[40vh] overflow-y-auto"
                >
                  {page?.length ? (
                    <>
                      {page.map((row: any, i: number) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={i}
                            onClick={(e) => {
                              if (showCheckbox) {
                                row.toggleRowSelected();
                              } else {
                                onSelectRow && onSelectRow(row);
                              }
                            }}
                            className="flex w-full bg-white border-b border-border-color-dark hover:bg-light-grey transition-all duration-300 ease-out cursor-pointer text-base"
                          >
                            {row.cells.map((cell: any, idx: number) => (
                              <td
                                {...cell.getCellProps()}
                                key={idx}
                                className="py-3 px-1 xl:px-4 text-xs xl:text-base flex-1"
                              >
                                {cell?.render('Cell')?.props?.value?.length > 12
                                  ? `${cell
                                      ?.render('Cell')
                                      ?.props?.value?.slice(0, 10)}..`
                                  : cell?.render('Cell')?.props?.value ||
                                    cell?.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </>
                  ) : (
                    <tr className="w-full flex">
                      <td
                        colSpan={headerGroups[0]?.headers.length}
                        className="w-full"
                      >
                        <NoDataShown />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {pagination && page?.length > 0 && (
                <div className="bg-white p-5 flex justify-between">
                  <div className="flex items-center space-x-5">
                    <button
                      onClick={() =>
                        isServerPagination
                          ? serverPagination.setPageIndex(
                              serverPagination.pageIndex - 1
                            )
                          : previousPage()
                      }
                      disabled={
                        isServerPagination
                          ? serverPagination.pageIndex === 0
                          : !canPreviousPage
                      }
                      className="bg-almost-white rounded-md h-6 w-6 pl-1 text-black"
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                    <button
                      onClick={() =>
                        isServerPagination
                          ? serverPagination.setPageIndex(
                              serverPagination.pageIndex + 1
                            )
                          : nextPage()
                      }
                      disabled={
                        isServerPagination
                          ? serverPagination.pageIndex + 1 >=
                            serverPagination.totalPages
                          : !canNextPage
                      }
                      className="bg-almost-white rounded-md h-6 w-6 pl-1 text-black ml-3"
                    >
                      <MdKeyboardArrowRight />
                    </button>
                    <span className="ml-5 text-black">
                      Page{' '}
                      {isServerPagination
                        ? serverPagination.pageIndex + 1
                        : pageIndex + 1}{' '}
                      of{' '}
                      {isServerPagination
                        ? serverPagination.totalPages
                        : pageOptions.length}
                    </span>
                  </div>

                  <div className="text-black">
                    <span className="text-sm text-text-black">
                      Results per page
                    </span>
                    <select
                      value={
                        isServerPagination
                          ? serverPagination.pageSize
                          : pageSize
                      }
                      onChange={(e) => {
                        const newSize = Number(e.target.value);
                        isServerPagination
                          ? serverPagination.setPageSize(newSize)
                          : setPageSize(newSize);
                      }}
                      className="font-normal px-1 text-sm rounded py-1 focus:outline-0 ml-2 bg-white border border-border-color-dark"
                    >
                      {[5, 10, 20, 30, 40, 50].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default React.memo(Table);
